import { Close } from '@mui/icons-material'
import {
  Dialog,
  DialogActions,
  DialogActionsProps,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Paper,
  PaperProps,
  Stack,
  styled,
  SxProps,
  Theme,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material'
import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { generateUUID } from '../utils/function'
import { CancelButton } from './CancelButton'

export const DraggableDialogContext = createContext(false)

const DraggablePaper: React.FC<PaperProps> = props => {
  const paperRef = useRef<HTMLDivElement>(null)
  const offset = useRef({ x: 0, y: 0 })

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    const titleEl = paperRef.current?.querySelector('.draggable-dialog-title') as HTMLElement | null
    if (!titleEl || !titleEl.contains(e.target as Node)) return

    const paper = paperRef.current
    if (!paper) return

    const rect = paper.getBoundingClientRect()
    offset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }

    const onPointerMove = (ev: PointerEvent) => {
      paper.style.position = 'fixed'
      paper.style.margin = '0'
      paper.style.left = `${ev.clientX - offset.current.x}px`
      paper.style.top = `${ev.clientY - offset.current.y}px`
    }

    const onPointerUp = () => {
      document.removeEventListener('pointermove', onPointerMove)
      document.removeEventListener('pointerup', onPointerUp)
    }

    document.addEventListener('pointermove', onPointerMove)
    document.addEventListener('pointerup', onPointerUp)
  }, [])

  return <Paper ref={paperRef} onPointerDown={onPointerDown} {...props} />
}

interface DialogContentProps {
  bgColor?: string
  minHeight?: string | number
  minWidth?: string | number
  maxWidth?: string | number
  maxHeight?: string | number
}

interface IGenericDialogProps extends DialogContentProps {
  title: string
  open: boolean
  onClose?: () => void
  bgColor?: string
  disableAutoClose?: boolean
  topActions?: React.ReactNode[]
  topActionsProps?: DialogActionsProps
  actions?: React.ReactNode[]
  removedCloseButton?: boolean
  fullWidth?: boolean
  sx?: SxProps<Theme> | undefined
  isWithLayout?: boolean
  fullScreen?: boolean | undefined
  titleTypography?: TypographyProps
  rootWidth?: string | number
  draggable?: boolean
}
export const GenericDialog: React.FC<IGenericDialogProps> = ({
  title,
  titleTypography,
  open,
  onClose,
  minHeight,
  minWidth,
  maxHeight,
  maxWidth,
  bgColor,
  disableAutoClose,
  topActions,
  topActionsProps,
  actions,
  removedCloseButton,
  fullWidth = false,
  children,
  sx,
  isWithLayout = false,
  fullScreen = false,
  rootWidth,
  draggable = false,
}) => {
  const { t } = useTranslation()
  const DefaultFormat = useMemo(() => {
    return (
      <>
        {title ? (
          <CustomDialogTitle
            onClose={onClose}
            isTopActionAvailable={Boolean(topActions)}
            titleTypography={titleTypography}
            draggable={draggable}
          >
            {title}
          </CustomDialogTitle>
        ) : null}
        <StyledDialogContent
          bgColor={bgColor}
          maxHeight={maxHeight}
          maxWidth={maxWidth}
          minHeight={minHeight}
          minWidth={minWidth}
          sx={{ paddingX: '28px', width: '100%', height: '100% ', ...sx }}
        >
          {children}
        </StyledDialogContent>
        {(!removedCloseButton || actions) && (
          <DialogActions
            {...topActionsProps}
            sx={{ paddingBottom: '16px', paddingTop: '0px', paddingRight: '36px', paddingLeft: '36px' }}
          >
            {[
              !removedCloseButton ? (
                <CancelButton key={generateUUID()} onClick={onClose} variant="outlined">
                  {t('cancel', { ns: 'common' })}
                </CancelButton>
              ) : (
                <></>
              ),
              ...(actions ? actions : []),
            ]}
          </DialogActions>
        )}
      </>
    )
  }, [actions, children, draggable, onClose, removedCloseButton, t, title, titleTypography, topActions, topActionsProps, bgColor, maxHeight, maxWidth, minHeight, minWidth, sx])
  return (
    <>
      <StyledDialog
        open={open}
        transitionDuration={{ enter: 180, exit: 120 }}
        onClose={(e, reason) => {
          if (disableAutoClose && reason === 'backdropClick') return
          if (onClose) onClose()
        }}
        disableEscapeKeyDown
        onBackdropClick={_ => {}}
        fullWidth={fullWidth}
        maxWidth={maxWidth as any}
        sx={sx}
        fullScreen={fullScreen}
        bgColor={bgColor}
        rootwidth={rootWidth}
        {...(draggable && { PaperComponent: DraggablePaper })}
      >
        <DraggableDialogContext.Provider value={draggable}>
          {isWithLayout ? children : DefaultFormat}
        </DraggableDialogContext.Provider>
      </StyledDialog>
    </>
  )
}

export interface DialogTitleProps {
  children?: React.ReactNode
  onClose?: () => void
  isTopActionAvailable: boolean
  titleTypography?: TypographyProps
  titleSectionTopPadding?: string
  titleSectionBottomPadding?: string
  titleSectionXPadding?: string
  closeIconSize?: string | number
  fontWeight?: number
  draggable?: boolean
}

export const CustomDialogTitle: React.FC<DialogTitleProps> = props => {
  const theme = useTheme()
  const {
    children,
    onClose,
    isTopActionAvailable,
    titleSectionTopPadding,
    titleSectionBottomPadding,
    titleSectionXPadding,
    titleTypography,
    closeIconSize,
    fontWeight,
    draggable: draggableProp,
    ...other
  } = props
  const draggableFromContext = useContext(DraggableDialogContext)
  const isDraggable = draggableProp ?? draggableFromContext
  return (
    <DialogTitle
      className={isDraggable ? 'draggable-dialog-title' : undefined}
      sx={{
        cursor: isDraggable ? 'move' : undefined,
        fontWeight: '600',
        color: getThemeColor(theme, MUIThemeColors.primaryMain),
        paddingTop: titleSectionTopPadding ?? '36px',
        paddingX: titleSectionXPadding ?? '36px',
        paddingBottom: !isTopActionAvailable ? titleSectionBottomPadding ?? '24px' : '0px',
        '& .MuiTypography-root': { fontWeight: fontWeight },
      }}
      {...other}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="h5"
          fontWeight={500}
          color={getThemeColor(theme, MUIThemeColors.black)}
          {...titleTypography}
        >
          {children}
        </Typography>
        {onClose ? (
          <IconButton aria-label="close" onClick={onClose}>
            <Close
              sx={{
                color: getThemeColor(theme, MUIThemeColors.black),
                fontSize: closeIconSize ? closeIconSize : undefined,
              }}
            />
          </IconButton>
        ) : null}
      </Stack>
    </DialogTitle>
  )
}

interface StyledDialogProps extends DialogProps {
  bgColor?: string
  rootwidth?: string | number
}

const StyledDialog = styled(Dialog)<StyledDialogProps>(({ bgColor, rootwidth }) => ({
  '& .MuiDialog-container': {
    '& .MuiPaper-root': {
      backgroundColor: bgColor ?? 'undefined',
      borderRadius: '8px',
      maxWidth: rootwidth ?? undefined,
    },
  },
}))

const StyledDialogContent = styled(DialogContent)<DialogContentProps>(
  ({ bgColor, minHeight, minWidth, maxHeight, maxWidth, theme }) => ({
    backgroundColor: bgColor ?? getThemeColor(theme, MUIThemeColors.white),
    maxWidth: maxWidth ?? '85vw',
    maxHeight: maxHeight ?? '95vh',
    minWidth: minWidth ?? '15vw',
    minHeight: minHeight ?? '5vh',
  }),
)
